import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import {
  webhooksFixture,
  deliveriesFixture,
  createWebhookFixture,
  updateWebhookFixture,
  deleteWebhookFixture,
  rotateWebhookFixture,
  redeliverFixture,
} from './fixture';

const INVALIDATE_WEBHOOKS = ['webhooks'];
const INVALIDATE_DELIVERIES = ['webhookDeliveries'];

/** GET /v1/admin/outbound-webhooks */
export function useWebhooks() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['webhooks', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return webhooksFixture();
      }
      return fetch('/v1/admin/outbound-webhooks');
    },
    staleTime: 30_000,
  });
}

/** GET /v1/admin/outbound-webhooks/deliveries — polling when in-flight items vorhanden */
export function useDeliveries({ webhookId, status }) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['webhookDeliveries', { webhookId, status }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return deliveriesFixture({ webhookId, status });
      }
      const params = new URLSearchParams();
      if (webhookId) params.set('webhookId', String(webhookId));
      if (status) params.set('status', status);
      params.set('limit', '100');
      return fetch(`/v1/admin/outbound-webhooks/deliveries?${params.toString()}`);
    },
    // 10s Polling solange pending|in_flight zu sehen ist (Spec-Empfehlung)
    refetchInterval: (q) => {
      const items = q.state.data?.deliveries ?? [];
      const hasInflight = items.some(
        (d) => d.status === 'pending' || d.status === 'in_flight',
      );
      return hasInflight ? 10_000 : false;
    },
  });
}

export function useCreateWebhook() {
  return useApiMutation({
    live: (fetch, payload) =>
      fetch('/v1/admin/outbound-webhooks', { method: 'POST', body: payload }),
    fixture: (payload) => createWebhookFixture(payload),
    invalidate: INVALIDATE_WEBHOOKS,
  });
}

export function useUpdateWebhook() {
  return useApiMutation({
    live: (fetch, { id, patch }) =>
      fetch(`/v1/admin/outbound-webhooks/${id}`, { method: 'PUT', body: patch }),
    fixture: ({ id, patch }) => updateWebhookFixture(id, patch),
    invalidate: INVALIDATE_WEBHOOKS,
    successMessage: 'Webhook gespeichert.',
  });
}

export function useDeleteWebhook() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/admin/outbound-webhooks/${id}`, { method: 'DELETE' }),
    fixture: ({ id }) => deleteWebhookFixture(id),
    invalidate: INVALIDATE_WEBHOOKS,
    successMessage: 'Webhook gelöscht.',
  });
}

export function useRotateWebhookSecret() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/admin/outbound-webhooks/${id}/rotate-secret`, { method: 'POST' }),
    fixture: ({ id }) => rotateWebhookFixture(id),
    invalidate: INVALIDATE_WEBHOOKS,
    // kein Toast — Plaintext kommt via OneShotSecretReveal
  });
}

export function useRedeliver() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/admin/outbound-webhooks/deliveries/${id}/redeliver`, {
        method: 'POST',
      }),
    fixture: ({ id }) => redeliverFixture(id),
    invalidate: INVALIDATE_DELIVERIES,
    successMessage: 'Re-Delivery eingereiht.',
  });
}
